import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Send, Loader2, CheckCircle2 } from "lucide-react";
import {
  PHONE_HREF,
  PHONE_DISPLAY,
  EMAIL,
  EMAIL_HREF,
} from "@/lib/contact";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    business: "",
    email: "",
    phone: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Thanks! We'll reach out shortly.");
      setForm({ name: "", business: "", email: "", phone: "", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please call us instead.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="section bg-nav-900/40 border-t border-nav-800"
    >
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Left: Info */}
          <div className="lg:col-span-5">
            <span className="eyebrow">
              <span className="h-1.5 w-1.5 rounded-full bg-brand" />
              Contact
            </span>
            <h2 className="mt-4 h-display text-4xl sm:text-5xl">
              Let's talk about your{" "}
              <span className="text-brand">next 100 booked clients.</span>
            </h2>
            <p className="mt-4 text-nav-300 text-lg">
              Tell us a bit about your business — services, where clients
              usually find you, and what you wish your booking flow looked like.
              We'll send back a sample funnel mocked up specifically for you.
            </p>

            <div className="mt-8 space-y-4">
              <a
                href={PHONE_HREF}
                data-testid="contact-phone-link"
                className="flex items-center gap-4 rounded-2xl border border-nav-800 bg-nav-900/70 p-5 hover:border-brand transition-colors"
              >
                <span className="grid place-items-center h-12 w-12 rounded-xl bg-brand text-white">
                  <Phone className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-wider text-nav-400 font-semibold">
                    Call us
                  </div>
                  <div className="font-display font-bold text-lg text-white">
                    {PHONE_DISPLAY}
                  </div>
                </div>
              </a>

              <a
                href={EMAIL_HREF}
                data-testid="contact-email-link"
                className="flex items-center gap-4 rounded-2xl border border-nav-800 bg-nav-900/70 p-5 hover:border-brand transition-colors"
              >
                <span className="grid place-items-center h-12 w-12 rounded-xl bg-nav-800 border border-nav-700 text-white">
                  <Mail className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-wider text-nav-400 font-semibold">
                    Email
                  </div>
                  <div className="font-display font-bold text-lg text-white">
                    {EMAIL}
                  </div>
                </div>
              </a>
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="rounded-2xl border border-nav-800 bg-nav-900/70 p-6 sm:p-8"
            >
              {done ? (
                <div
                  data-testid="contact-form-success"
                  className="flex flex-col items-center text-center py-12"
                >
                  <span className="grid place-items-center h-14 w-14 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                    <CheckCircle2 className="h-7 w-7" />
                  </span>
                  <h3 className="mt-5 font-display font-bold text-2xl text-white">
                    Thanks! We'll reach out shortly.
                  </h3>
                  <p className="mt-2 text-nav-300 max-w-sm">
                    Thanks for reaching out — we'll get back to you the same
                    business day. Need it now? Just call {PHONE_DISPLAY}.
                  </p>
                  <button
                    type="button"
                    onClick={() => setDone(false)}
                    data-testid="contact-form-reset"
                    className="mt-6 text-sm font-semibold text-brand hover:underline"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field
                      label="Your name"
                      name="name"
                      value={form.name}
                      onChange={onChange}
                      placeholder="John Smith"
                      required
                      testId="contact-input-name"
                    />
                    <Field
                      label="Business name"
                      name="business"
                      value={form.business}
                      onChange={onChange}
                      placeholder="Bloom Lash Studio"
                      testId="contact-input-business"
                    />
                    <Field
                      label="Email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={onChange}
                      placeholder="you@business.com"
                      required
                      testId="contact-input-email"
                    />
                    <Field
                      label="Phone or Instagram (optional)"
                      name="phone"
                      type="tel"
                      value={form.phone}
                      onChange={onChange}
                      placeholder="@yourstudio or 555-123-4567"
                      testId="contact-input-phone"
                    />
                  </div>

                  <label className="block mt-4">
                    <span className="text-sm font-semibold text-nav-200">
                      What do you need?{" "}
                      <span className="text-brand">*</span>
                    </span>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      rows={5}
                      required
                      data-testid="contact-input-message"
                      placeholder="What services do you offer? Where do clients usually find you (Instagram, Google, referrals)? What does your current booking flow look like?"
                      className="input-dark"
                    />
                  </label>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit"
                    className="btn-primary w-full mt-5"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        <Send className="h-5 w-5" />
                        Send Message
                      </>
                    )}
                  </button>
                  <p className="mt-3 text-xs text-nav-400 text-center">
                    Prefer to talk? Call{" "}
                    <a
                      href={PHONE_HREF}
                      className="text-brand font-semibold underline"
                    >
                      {PHONE_DISPLAY}
                    </a>{" "}
                    — usually picks up.
                  </p>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, type = "text", testId, ...rest }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-nav-200">
        {label}
        {rest.required ? <span className="text-brand"> *</span> : null}
      </span>
      <input
        name={name}
        type={type}
        data-testid={testId}
        className="input-dark"
        {...rest}
      />
    </label>
  );
}
